import { axios, AxiosResponse } from "@sdk/axios";
import { SDKConfig } from "@sdk/default-sdk-config";
import { EntityServices } from "@sdk/utils/entity.service";
import type { PaginatedOptions, PaginatedResults } from "@sdk/utils/paginated-results";
import { UserTracker } from "user-tracker";
import type {
  iAmendment,
  iBooking,
  iBookingData,
  iEnquiryData,
  iPassenger,
  iPaymentScheduleItem,
  iProduct,
  iProductAccommodation,
  iProductAttraction,
  iProductCar,
  iProductCruise,
  iProductExcursion,
  iProductFlight,
  iProductPriceDetails,
  iProductRail,
  iProductTour,
  iProductTransfer,
  iPricingAndPaymentsPlan,
  iCustomerInvoice,
  CustomerPaymentReceiptCreateInput,
  iProductVoucher,
  iBookingEmailMessage,
  iBookingAtol,
} from "./booking-model";
import type {
  ProfitAllocationRecordCreateInput,
  ProfitAllocationRecordPatchInput,
} from "@sdk/profit-allocation-records/profit-allocation-record-model";
import type { iSupplierBill } from "@sdk/supplier-bills/supplier-bill-model";
import type {
  CustomerPaymentReportRowV2,
} from "./bookings-reports.models";

export type BulkImportPaymentReceiptInput = CustomerPaymentReceiptCreateInput & {
  bookingRef: string;
  originalCurrency: string;
  originalAmount: number;
};

export type BulkImportPaymentReceiptsResult = {
  operationId: string;
  successCount: number;
  failureCount: number;
  skippedCount: number;
  failedBillIds: string[];
  failedRecords: Array<{
    record: BulkImportPaymentReceiptInput;
    reason: string;
  }>;
  skippedRecords: Array<{
    record: BulkImportPaymentReceiptInput;
    reason: string;
    bookingId?: string;
    receiptId?: string;
  }>;
  results: Array<{
    bookingRef: string;
    bookingId?: string;
    receiptId?: string;
    skipped?: boolean;
    error?: string;
  }>;
};

export class BookingsServices {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
  }
  constructor(protected config: { basePath: string; token: string }) {
    this.bookings.configure(config);
  }

  bookings = Object.assign(
    new EntityServices<iBooking>(SDKConfig, "bookings", {
      onCreate: () => {
        UserTracker.track("BOOKINGS - Create", {});
      },
      onDelete: () => {
        UserTracker.track("BOOKINGS - Delete", {});
      },
    }),
    {
      hardReset: async (
        confirmation: string
      ): Promise<{ status: boolean; deletedCounts: Record<string, number> }> => {
        const results = await axios.post<{
          status: boolean;
          deletedCounts: Record<string, number>;
        }>(
          this.config.basePath + `/bookings/hard-reset`,
          { confirmation },
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },

      convertToHomeCurrency: async (body: {
        homeCurrency: string;
        fromCurrency: string;
        amount: number;
        dateTs?: number;
      }): Promise<{
        amountHomeCurrency: number;
        rate: number;
        timestamp: number;
        day: string;
        fromCache: boolean;
      }> => {
        const timestamp = body.dateTs ?? Date.now();

        // Avoid the network hop when no FX conversion is needed.
        if (body.homeCurrency === body.fromCurrency) {
          const day = new Date(timestamp).toISOString().slice(0, 10);
          return {
            amountHomeCurrency: Math.round(body.amount * 100) / 100,
            rate: 1,
            timestamp,
            day,
            fromCache: true,
          };
        }

        const results = await axios.post<{
          rate: number;
          timestamp: number;
          day: string;
          fromCache: boolean;
        }>(
          this.config.basePath + `/exchange-rates/resolve-home-rate`,
          {
            homeCurrency: body.homeCurrency,
            fromCurrency: body.fromCurrency,
            dateTs: timestamp,
          },
          { headers: { Authorization: this.config.token } }
        );

        return {
          amountHomeCurrency: body.amount/results.data.rate,
          ...results.data,
        };
      },

      // Amendments
      addAmendmentRequest: async (
        bookingId: string,
        body: {
          amendmentRequestBody: string;
          affectedProducts?: string[];
          type?: iAmendment["type"];
        }
      ) => {
        UserTracker.track("BOOKINGS - Add Amendment Request");
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/amendments/request`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      addAmendment: async (
        bookingId: string,
        amendmentId: string,
        body: {
          body?: string;
          type?: iAmendment["type"];
          affectedProducts?: string[];
          products?: iProduct[];
          amendedPrice?: iPricingAndPaymentsPlan | null;
          updatedPassengerDetails?: iPassenger[] | null;
          cancelledPassengerIds?: string[] | null;
          cancellationReason?: string | null;
          refundDetails?: iAmendment["refundDetails"] | null;
        }
      ) => {
        UserTracker.track("BOOKINGS - Add Amendment");
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/amendments/${amendmentId}`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      confirmAmendment: async (
        bookingId: string,
        amendmentId: string,
        body: { confirmationBody: string; confirmationTrace?: unknown }
      ) => {
        UserTracker.track("BOOKINGS - Confirm Amendment");
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath +
          `/bookings/${bookingId}/amendments/${amendmentId}/confirm`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      discardAmendmentRequest: async (
        bookingId: string,
        amendmentId: string
      ) => {
        UserTracker.track("BOOKINGS - Discard Amendment Request");
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath +
          `/bookings/${bookingId}/amendments/${amendmentId}/discard`,
          {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Discussion — use SDK.bookingDiscussionItems
      // Activity log — use SDK.bookingActivityLogs
      // Payment Receipts
      addPaymentReceipt: async (
        bookingId: string,
        receipt: CustomerPaymentReceiptCreateInput
      ) => {
        UserTracker.track("BOOKINGS - Add Payment Receipt");
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/payment-receipts`,
          receipt,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      previewPaymentReceiptJournals: async (
        bookingId: string,
        receipt: CustomerPaymentReceiptCreateInput
      ) => {
        const results: AxiosResponse<
          import("@sdk/payment-receipt-journal-preview-model").PaymentReceiptJournalPreviewResult
        > = await axios.post(
          this.config.basePath +
            `/bookings/${bookingId}/payment-receipts/preview-journals`,
          receipt,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      applyQuoteDepositReceipt: async (
        bookingId: string,
        body?: { paymentMethodId?: string; paymentScheduleItemId?: string }
      ) => {
        UserTracker.track("BOOKINGS - Apply Quote Deposit Receipt");
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath +
            `/bookings/${bookingId}/apply-quote-deposit-receipt`,
          body ?? {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      removePaymentReceipt: async (
        bookingId: string,
        receiptId: string
      ) => {
        const results: AxiosResponse<iBooking> = await axios.delete(
          this.config.basePath +
          `/bookings/${bookingId}/payment-receipts/${receiptId}`,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      voidPaymentReceipt: async (
        bookingId: string,
        receiptId: string
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/payment-receipts/${receiptId}/void`,
          {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      previewVoidBooking: async (bookingId: string) => {
        UserTracker.track("BOOKINGS - Preview Void Booking");
        const results: AxiosResponse<
          import("@sdk/bookings/booking-void-preview-model").BookingVoidPreviewResult
        > = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/void/preview`,
          {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      voidBooking: async (bookingId: string) => {
        UserTracker.track("BOOKINGS - Void Booking");
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/void`,
          {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      previewCloseBooking: async (bookingId: string) => {
        UserTracker.track("BOOKINGS - Preview Close Booking");
        const results: AxiosResponse<
          import("@sdk/bookings/booking-close-preview-model").BookingClosePreviewResult
        > = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/close/preview`,
          {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      closeBooking: async (
        bookingId: string,
        body?: import("@sdk/bookings/booking-close-preview-model").BookingCloseInput
      ) => {
        UserTracker.track("BOOKINGS - Close Booking");
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/close`,
          body ?? {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updatePaymentReceiptRemark: async (
        bookingId: string,
        receiptId: string,
        body: { remark?: string; remarkAttachments?: string[]; paymentScheduleItemId?: string }
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/payment-receipts/${receiptId}/remark`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      bulkImportPaymentReceipts: async (
        rows: BulkImportPaymentReceiptInput[]
      ): Promise<BulkImportPaymentReceiptsResult> => {
        const results = await axios.post<BulkImportPaymentReceiptsResult>(
          this.config.basePath + `/bookings/payment-receipts/bulk-import`,
          rows,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      reconcilePDQReceipts: async (body: {
        receiptIds: string[];
        ledgerId: string;
        pdqCharges: number;
        totalReceivedToBank: number;
        occurredAt?: number;
        description?: string;
        notes?: string;
      }): Promise<{
        status: true;
        reconciledReceiptIds: string[];
        bankTransactionId: string;
        journalEntryIds?: string[];
        reconciliationSessionId?: string;
        statementImportLineId?: string;
      }> => {
        const results = await axios.post<{
          status: true;
          reconciledReceiptIds: string[];
          bankTransactionId: string;
          journalEntryIds?: string[];
          reconciliationSessionId?: string;
          statementImportLineId?: string;
        }>(
          this.config.basePath + `/bookings/payment-receipts/reconcile-pdq`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Payment reminders
      sendPaymentReminderEmail: async (
        bookingId: string,
        body: { scheduleId: string }
      ): Promise<{ sent: boolean; to: string }> => {
        const results = await axios.post<{ sent: boolean; to: string }>(
          this.config.basePath + `/bookings/${bookingId}/send-payment-reminder-email`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      getPreviewPaymentReminderEmail: async (
        bookingId: string,
        params: { scheduleId: string;}
      ): Promise<{ to: string; subjectline: string; body: string }> => {
        const search = new URLSearchParams({ scheduleId: params.scheduleId });
        const results = await axios.get<{ to: string; subjectline: string; body: string }>(
          this.config.basePath + `/bookings/${bookingId}/preview-payment-reminder-email?${search}`,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      triggerSchedulePaymentReminderEmails: async (
        bookingId: string
      ): Promise<{ scheduled: boolean }> => {
        const results = await axios.post<{ scheduled: boolean }>(
          this.config.basePath + `/bookings/${bookingId}/schedule-payment-reminder-emails`,
          {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      setPaymentReminderEmailsPaused: async (
        bookingId: string,
        body: { isPaused: boolean }
      ): Promise<{ paymentReminderEmails: iCustomerInvoice["paymentReminderEmails"] }> => {
        const results = await axios.patch<{
          paymentReminderEmails: iCustomerInvoice["paymentReminderEmails"];
        }>(
          this.config.basePath + `/bookings/${bookingId}/payment-reminder-emails-pause`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Booking data
      updateBookingData: async (
        bookingId: string,
        body: Partial<iBookingData>
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/booking-data`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateAtol: async (bookingId: string, body: Partial<iBookingAtol>) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/atol`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateEnquiryData: async (
        bookingId: string,
        body: Partial<iEnquiryData>
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/enquiry-data`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updatePassengers: async (
        bookingId: string,
        body: iPassenger[]
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/passengers`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updatePassengerDetail: async (
        bookingId: string,
        passengerId: string,
        body: Partial<
          Pick<
            iPassenger,
            | "email"
            | "phoneNumber"
            | "notes"
            | "travelDocumentType"
            | "passportNationality"
            | "passportType"
            | "passportImage"
            | "specialRequest"
          >
        >
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/passengers/${passengerId}`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Payment Schedule
      updatePaymentSchedule: async (
        bookingId: string,
        body: { paymentSchedule: iPaymentScheduleItem[] }
      ) => {
        UserTracker.track("BOOKINGS - Update Payment Schedule");
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/payment-schedule`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Draft pricing and payment plan (pre-confirmation only)
      updateDraftPricingAndPayments: async (
        bookingId: string,
        body: iPricingAndPaymentsPlan & { notes?: string }
      ) => {
        UserTracker.track("BOOKINGS - Update Draft Pricing and Payments");
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
            `/bookings/${bookingId}/pricing-and-payments/draft`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateCustomerInvoicePricingAndPayments: async (
        bookingId: string,
        body: iPricingAndPaymentsPlan & { notes?: string }
      ): Promise<iCustomerInvoice> => {
        UserTracker.track("BOOKINGS - Update Customer Invoice Pricing");
        const results: AxiosResponse<iCustomerInvoice> = await axios.patch(
          this.config.basePath +
            `/bookings/${bookingId}/customer-invoice/pricing-and-payments`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateCustomerInvoicePaymentSchedule: async (
        bookingId: string,
        body: { paymentSchedule: iPaymentScheduleItem[]; notes?: string }
      ): Promise<iCustomerInvoice> => {
        UserTracker.track("BOOKINGS - Update Customer Invoice Payment Schedule");
        const results: AxiosResponse<iCustomerInvoice> = await axios.patch(
          this.config.basePath +
            `/bookings/${bookingId}/customer-invoice/payment-schedule`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      allocateBookingProfit: async (
        bookingId: string,
        body: {
          records: ProfitAllocationRecordCreateInput[];
          isReallocation?: boolean;
        }
      ): Promise<iBooking> => {
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath +
            `/bookings/${bookingId}/profit-allocation/allocate`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      patchProfitAllocationRecord: async (
        bookingId: string,
        recordId: string,
        body: Partial<ProfitAllocationRecordPatchInput> = {}
      ): Promise<iBooking> => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
            `/bookings/${bookingId}/profit-allocation/records/${recordId}`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Flags
      raiseFlag: async (
        bookingId: string,
        flagColor: "red" | "blue",
        body: { comment: string; requireValidation?: boolean; requireComment?: boolean }
      ) => {
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/flags/${flagColor}/raise`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      dismissFlag: async (
        bookingId: string,
        flagColor: "red" | "blue",
        body?: { comment?: string }
      ) => {
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/flags/${flagColor}/dismiss`,
          body ?? {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Products
      addProduct: async (
        bookingId: string,
        product: Omit<iProduct, "id">
      ) => {
        UserTracker.track("BOOKINGS - Add Product");
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/products`,
          product,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      reorderProducts: async (
        bookingId: string,
        productIds: string[]
      ): Promise<iBooking> => {
        UserTracker.track("BOOKINGS - Reorder Products");
        const results: AxiosResponse<iBooking> = await axios.put(
          this.config.basePath + `/bookings/${bookingId}/reorder-products`,
          { productIds },
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      reorderAmendmentProducts: async (
        bookingId: string,
        amendmentId: string,
        productIds: string[]
      ): Promise<iBooking> => {
        UserTracker.track("BOOKINGS - Reorder Amendment Products");
        const results: AxiosResponse<iBooking> = await axios.put(
          this.config.basePath +
            `/bookings/${bookingId}/amendments/${amendmentId}/reorder-products`,
          { productIds },
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      voidProduct: async (bookingId: string, productId: string) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/products/${productId}/void`,
          {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateProductPriceDetails: async (
        bookingId: string,
        productId: string,
        priceDetails: Partial<iProductPriceDetails>
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/products/${productId}/price-details`,
          priceDetails,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateProductSupplierDetails: async (
        bookingId: string,
        productId: string,
        data: {
          supplierId?: string;
          supplierBookingReference?: string;
          supplierBookingInternalNotes?: string;
          priceDetails?: Partial<iProductPriceDetails>;
        },
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/products/${productId}/supplier-details`,
          data,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateProductData: async (
        bookingId: string,
        productId: string,
        data: Partial<
          Pick<
            iProduct,
            | "flight"
            | "accommodation"
            | "cruise"
            | "car"
            | "transfer"
            | "attraction"
            | "excursion"
            | "rail"
            | "tour"
            | "customerNotes"
            | "customerNotesAttachments"
            | "internalNotes"
            | "internalNotesAttachments"
            | "supplierProductReference"
            | "isHiddenProduct"
          >
        >
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/products/${productId}/data`,
          data,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateProductInternalNotes: async (
        bookingId: string,
        productId: string,
        body: {
          internalNotesAttachments?: string[];
          internalNotes?: string;
          supplierProductReference?: string;
        }
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/products/${productId}/internal-notes`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateLinkedPassengerIds: async (
        bookingId: string,
        productId: string,
        body: { linkedPassengerIds?: string[] }
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/products/${productId}/linked-passenger-ids`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateLinkedProduct: async (
        bookingId: string,
        productId: string,
        body: { parentProductId?: string | null }
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/products/${productId}/linked-product`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      billProduct: async (
        bookingId: string,
        productId: string,
        body?: {
          supplierId?: string;
          supplierBookingReference?: string;
          supplierBookingInternalNotes?: string;
          priceDetails?: Partial<iProductPriceDetails>;
          paymentSchedule?: iPaymentScheduleItem[];
        }
      ): Promise<{ booking: iBooking; supplierBill: iSupplierBill }> => {
        const results = await axios.post<{
          booking: iBooking;
          supplierBill: iSupplierBill;
        }>(
          this.config.basePath +
            `/bookings/${bookingId}/products/${productId}/bill`,
          body ?? {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      billAllProducts: async (
        bookingId: string,
        body: {
          products: Array<{
            productId: string;
            supplierId?: string;
            supplierBookingReference?: string;
            supplierBookingInternalNotes?: string;
            priceDetails?: Partial<iProductPriceDetails>;
            paymentSchedule?: iPaymentScheduleItem[];
          }>;
        }
      ): Promise<{ booking: iBooking; supplierBills: iSupplierBill[] }> => {
        const results = await axios.post<{
          booking: iBooking;
          supplierBills: iSupplierBill[];
        }>(
          this.config.basePath + `/bookings/${bookingId}/bill-all-products`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateBilledProductSupplier: async (
        bookingId: string,
        productId: string,
        body: {
          supplierBookingReference?: string;
          supplierBookingInternalNotes?: string;
          priceDetails?: Partial<iProductPriceDetails>;
        }
      ) => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
            `/bookings/${bookingId}/products/${productId}/billed-supplier`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      cancelSupplierBill: async (
        bookingId: string,
        productId: string
      ): Promise<{ booking: iBooking; supplierBill: iSupplierBill }> => {
        const results = await axios.post<{
          booking: iBooking;
          supplierBill: iSupplierBill;
        }>(
          this.config.basePath +
            `/bookings/${bookingId}/products/${productId}/cancel-supplier-bill`,
          {},
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Product voucher (single voucher on product)
      addProductVoucher: async (
        bookingId: string,
        productId: string,
        body: Partial<Pick<iProductVoucher, "isReady" | "body" | "attachments" | "voucherDueDate">>
      ) => {
        UserTracker.track("BOOKINGS - Add/Update Product Voucher");
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath +
          `/bookings/${bookingId}/products/${productId}/voucher`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Booking confirmation email preview and send
      getBookingConfirmationEmailPreview: async (
        id: string,
        params?: {
          includeStripePaymentLink?: boolean;
          stripePaymentMode?: "first_tranche" | "full_balance";
          hideCustomerRequest?: boolean;
        }
      ): Promise<{ to: string; subjectline: string; body: string }> => {
        const search = new URLSearchParams();
        if (params?.includeStripePaymentLink === true) {
          search.set("includeStripePaymentLink", "true");
        } else if (params?.includeStripePaymentLink === false) {
          search.set("includeStripePaymentLink", "false");
        }
        if (params?.stripePaymentMode) {
          search.set("stripePaymentMode", params.stripePaymentMode);
        }
        if (params?.hideCustomerRequest === true) {
          search.set("hideCustomerRequest", "true");
        } else if (params?.hideCustomerRequest === false) {
          search.set("hideCustomerRequest", "false");
        }
        const qs = search.toString();
        const results = await axios.get<{
          to: string;
          subjectline: string;
          body: string;
        }>(
          this.config.basePath +
            `/bookings/${id}/booking-confirmation-email-preview` +
            (qs ? `?${qs}` : ""),
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      confirmBooking: async (
        id: string,
        body: {
          remark: string;
          attachments?: string[];
        }
      ): Promise<iBooking> => {
        UserTracker.track("BOOKINGS - Confirm Booking");
        const results: AxiosResponse<iBooking> = await axios.post(
          this.config.basePath + `/bookings/${id}/confirm-booking`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      sendConfirmation: async (
        id: string,
        body: {
          cc?: string | string[];
          subjectline?: string;
          includeStripePaymentLink?: boolean;
          stripePaymentMode?: "first_tranche" | "full_balance";
          hideCustomerRequest?: boolean;
        } = {},
      ): Promise<{ sent: boolean; to: string }> => {
        const results = await axios.post<{ sent: boolean; to: string }>(
          this.config.basePath + `/bookings/${id}/send-confirmation`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Customer receipt email preview and send
      getCustomerReceiptEmailPreview: async (
        id: string,
        params: { PDFUrl?: string } = {}
      ): Promise<{ to: string; subjectline: string; body: string }> => {
        const search = new URLSearchParams();
        if (params.PDFUrl) search.set("PDFUrl", params.PDFUrl);
        const qs = search.toString();
        const results = await axios.get<{
          to: string;
          subjectline: string;
          body: string;
        }>(
          this.config.basePath +
            `/bookings/${id}/customer-receipt-email-preview${qs ? `?${qs}` : ""}`,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      sendCustomerReceipt: async (
        id: string,
        body: { PDFUrl?: string; cc?: string | string[]; subjectline?: string } = {}
      ): Promise<{ sent: boolean; to: string }> => {
        const results = await axios.post<{ sent: boolean; to: string }>(
          this.config.basePath + `/bookings/${id}/send-customer-receipt`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Voucher email preview
      getVoucherEmailPreview: async (
        id: string
      ): Promise<{ to: string; subjectline: string; body: string }> => {
        const results = await axios.get<{
          to: string;
          subjectline: string;
          body: string;
        }>(
          this.config.basePath + `/bookings/${id}/voucher-email-preview`,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      sendVoucherEmail: async (
        id: string, body: { cc?: string | string[]; subjectline?: string } = {}
      ): Promise<{ sent: boolean; to: string }> => {
        const results = await axios.post<{ sent: boolean; to: string }>(
          this.config.basePath + `/bookings/${id}/send-voucher-email`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Assign / unassign / owner
      assignUser: async (
        bookingId: string,
        body: { userId: string }
      ): Promise<iBooking> => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/assign-user`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      unassignUser: async (
        bookingId: string,
        body: { userId: string }
      ): Promise<iBooking> => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/unassign-user`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      updateOwner: async (
        bookingId: string,
        body: { userId: string }
      ): Promise<iBooking> => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/update-owner`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },

      updateAdminTags: async (
        bookingId: string,
        body: { adminTags: string[] }
      ): Promise<iBooking> => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/admin-tags`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },

      updateTags: async (
        bookingId: string,
        body: { tags: string[] }
      ): Promise<iBooking> => {
        const results: AxiosResponse<iBooking> = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/tags`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      // Email messages
      getAllEmailMessages: async (
        bookingId: string,
        params: { page?: number; limit?: number } = {}
      ): Promise<{
        docs: iBookingEmailMessage[];
        totalDocs: number;
        page: number;
        limit: number;
        totalPages: number;
        hasPrevPage: boolean;
        hasNextPage: boolean;
      }> => {
        const page = Math.max(1, Number(params.page) || 1);
        const limit = Math.min(Math.max(1, Number(params.limit) || 20), 100);
        const search = new URLSearchParams({ page: String(page), limit: String(limit) });
        const results = await axios.get(
          this.config.basePath + `/bookings/${bookingId}/email-messages?${search}`,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      replyToEmailMessage: async (
        bookingId: string,
        body: { messageId: string; body: string }
      ): Promise<{ sent: true; messageId?: string }> => {
        const results = await axios.post(
          this.config.basePath + `/bookings/${bookingId}/email-messages/reply`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      markEmailMessageRead: async (
        bookingId: string,
        body: { messageId: string }
      ): Promise<{ updated: true }> => {
        const results = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/email-messages/mark-read`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },
      markEmailMessageUnread: async (
        bookingId: string,
        body: { messageId: string }
      ): Promise<{ updated: true }> => {
        const results = await axios.patch(
          this.config.basePath + `/bookings/${bookingId}/email-messages/mark-unread`,
          body,
          { headers: { Authorization: this.config.token } }
        );
        return results.data;
      },

      // Reports (legacy — PDQ only; performance reports use SDK.reports.performance)
      reports: {
        pdqReconciliation: async (body: {
          query?: Record<string, unknown>;
          options?: PaginatedOptions;
        }): Promise<PaginatedResults<CustomerPaymentReportRowV2>> => {
          const results = await axios.post<PaginatedResults<CustomerPaymentReportRowV2>>(
            this.config.basePath + `/accounting/reports/pdq-reconciliation`,
            body,
            { headers: { Authorization: this.config.token } }
          );
          return results.data;
        },
      },
    }
  );


}